import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Inbox, Star, ImageIcon, Share2, Trash2, MailOpen, Upload, Linkedin, Instagram, Save } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stars = ({ n }) => (
  <div className="flex gap-0.5">
    {Array.from({ length: 5 }, (_, i) => (
      <Star key={i} className={`w-3.5 h-3.5 ${i < n ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground/30'}`} />
    ))}
  </div>
);

export default function LandingInboxPage() {
  const [tab, setTab] = useState('messages');
  const [messages, setMessages] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [media, setMedia] = useState([]);
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const fileRefs = useRef({});

  const loadAll = () => {
    axios.get(`${API_URL}/landing/admin/messages`).then(r => setMessages(r.data)).catch(() => {});
    axios.get(`${API_URL}/landing/admin/reviews`).then(r => setReviews(r.data)).catch(() => {});
    axios.get(`${API_URL}/landing/admin/media`).then(r => setMedia(r.data)).catch(() => {});
    axios.get(`${API_URL}/landing/admin/settings`).then(r => setSettings(r.data)).catch(() => toast.error('Missing "manage_landing" permission?'));
  };
  useEffect(loadAll, []);

  const markRead = async (id) => { await axios.put(`${API_URL}/landing/admin/messages/${id}/read`); loadAll(); };
  const deleteMessage = async (id) => { if (!window.confirm('Delete this message?')) return; await axios.delete(`${API_URL}/landing/admin/messages/${id}`); loadAll(); };
  const toggleReview = async (r) => {
    await axios.put(`${API_URL}/landing/admin/reviews/${r.id}`, { published: !r.published });
    toast.success(`Review ${!r.published ? 'published' : 'unpublished'}`);
    loadAll();
  };
  const deleteReview = async (id) => { if (!window.confirm('Delete this review?')) return; await axios.delete(`${API_URL}/landing/admin/reviews/${id}`); loadAll(); };

  const uploadMedia = async (key, file) => {
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(`${API_URL}/landing/admin/media/${key}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Image uploaded');
      loadAll();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Upload failed'); }
  };
  const clearMedia = async (key) => { await axios.delete(`${API_URL}/landing/admin/media/${key}`); loadAll(); };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API_URL}/landing/admin/settings`, settings);
      toast.success('Settings saved');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    finally { setSavingSettings(false); }
  };

  const unreadCount = messages.filter(m => !m.read).length;
  const publishedCount = reviews.filter(r => r.published).length;

  return (
    <div className="space-y-5" data-testid="landing-inbox-page">
      <div>
        <h1 className="text-3xl font-heading font-semibold flex items-center gap-2"><Inbox className="w-7 h-7 text-primary" />Landing Page</h1>
        <p className="text-sm text-muted-foreground">Contact messages, reviews moderation, media replacements and social links.</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="messages" data-testid="tab-messages">Messages{unreadCount > 0 && <span className="ml-1.5 text-xs px-1.5 py-0.5 rounded-full bg-primary text-primary-foreground">{unreadCount}</span>}</TabsTrigger>
          <TabsTrigger value="reviews" data-testid="tab-reviews">Reviews <span className="ml-1.5 text-xs text-muted-foreground">({publishedCount}/{reviews.length} published)</span></TabsTrigger>
          <TabsTrigger value="media" data-testid="tab-media">Media</TabsTrigger>
          <TabsTrigger value="social" data-testid="tab-social">Social</TabsTrigger>
        </TabsList>

        <TabsContent value="messages" className="mt-4 space-y-2">
          {messages.length === 0 ? <p className="text-muted-foreground text-sm py-10 text-center">No contact messages yet.</p> :
            messages.map(m => (
              <div key={m.id} className={`p-4 rounded-lg border ${m.read ? 'border-border bg-white' : 'border-primary/50 bg-primary/5'}`} data-testid={`msg-${m.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm">{m.name} · <a href={`mailto:${m.email}`} className="text-primary hover:underline">{m.email}</a>{m.phone && ` · ${m.phone}`}</div>
                    {m.subject && <div className="text-xs text-muted-foreground">Subject: {m.subject}</div>}
                    <p className="text-sm mt-2 whitespace-pre-wrap">{m.message}</p>
                    <div className="text-xs text-muted-foreground mt-2">{new Date(m.created_at).toLocaleString()}</div>
                  </div>
                  <div className="flex gap-1">
                    {!m.read && <Button size="sm" variant="ghost" onClick={() => markRead(m.id)} data-testid={`msg-read-${m.id}`}><MailOpen className="w-4 h-4" /></Button>}
                    <Button size="sm" variant="ghost" onClick={() => deleteMessage(m.id)} data-testid={`msg-del-${m.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                  </div>
                </div>
              </div>
            ))}
        </TabsContent>

        <TabsContent value="reviews" className="mt-4 space-y-3">
          {settings && (
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/40">
              <div>
                <div className="font-medium text-sm">Display reviews on landing page</div>
                <p className="text-xs text-muted-foreground">When ON, published reviews appear below Contact Us.</p>
              </div>
              <Switch
                checked={!!settings.reviews_display_enabled}
                onCheckedChange={async (v) => { setSettings(s => ({...s, reviews_display_enabled: v})); await axios.put(`${API_URL}/landing/admin/settings`, { reviews_display_enabled: v }); toast.success('Updated'); }}
                data-testid="reviews-display-toggle"
              />
            </div>
          )}
          {reviews.length === 0 ? <p className="text-muted-foreground text-sm py-10 text-center">No reviews yet.</p> :
            reviews.map(r => (
              <div key={r.id} className={`p-4 rounded-lg border ${r.published ? 'border-emerald-300 bg-emerald-50/50' : 'border-border bg-white'}`} data-testid={`review-${r.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-sm font-medium">{r.name} <Stars n={r.rating} /></div>
                    {r.email && <div className="text-xs text-muted-foreground">{r.email}</div>}
                    <p className="text-sm mt-2 whitespace-pre-wrap">{r.comment}</p>
                    <div className="text-xs text-muted-foreground mt-2">{new Date(r.created_at).toLocaleString()}</div>
                  </div>
                  <div className="flex flex-col gap-1 items-end">
                    <label className="flex items-center gap-2 text-xs cursor-pointer">
                      <Switch checked={r.published} onCheckedChange={() => toggleReview(r)} data-testid={`review-publish-${r.id}`} />
                      Publish
                    </label>
                    <Button size="sm" variant="ghost" onClick={() => deleteReview(r.id)} data-testid={`review-del-${r.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                  </div>
                </div>
              </div>
            ))}
        </TabsContent>

        <TabsContent value="media" className="mt-4">
          <p className="text-sm text-muted-foreground mb-3">Upload replacement images for landing sections. Changes appear immediately.</p>
          <div className="grid md:grid-cols-2 gap-4">
            {media.map(m => (
              <div key={m.key} className="p-4 rounded-lg border border-border bg-white space-y-3" data-testid={`media-${m.key}`}>
                <div>
                  <div className="text-sm font-medium">{m.label}</div>
                  <div className="text-xs text-muted-foreground">Key: <code>{m.key}</code></div>
                </div>
                {m.image_url ? (
                  <img src={m.image_url} alt={m.label} className="w-full h-40 object-cover rounded-lg border border-border" />
                ) : (
                  <div className="w-full h-40 rounded-lg border border-dashed border-border flex items-center justify-center text-muted-foreground text-xs"><ImageIcon className="w-6 h-6" /></div>
                )}
                <input ref={(el) => (fileRefs.current[m.key] = el)} type="file" accept="image/*" hidden onChange={(e) => e.target.files?.[0] && uploadMedia(m.key, e.target.files[0])} />
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="flex-1" onClick={() => fileRefs.current[m.key]?.click()} data-testid={`media-upload-${m.key}`}><Upload className="w-4 h-4 mr-1" />{m.image_url ? 'Replace' : 'Upload'}</Button>
                  {m.image_url && <Button size="sm" variant="ghost" onClick={() => clearMedia(m.key)} data-testid={`media-del-${m.key}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>}
                </div>
                {m.updated_at && <div className="text-[10px] text-muted-foreground">Last updated: {new Date(m.updated_at).toLocaleString()}</div>}
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="social" className="mt-4 max-w-2xl space-y-4">
          {settings && (
            <>
              <div className="flex items-center gap-2">
                <Share2 className="w-5 h-5 text-primary" />
                <h2 className="font-semibold">Social feed links</h2>
              </div>
              <p className="text-xs text-muted-foreground">Add your profile URL and paste up to 3 latest post URLs per platform — they'll appear in the landing page social section.</p>
              <div className="p-4 rounded-lg border border-border space-y-3">
                <div className="flex items-center gap-2 font-medium"><Linkedin className="w-4 h-4 text-blue-700" />LinkedIn</div>
                <div>
                  <Label>Company page URL</Label>
                  <Input value={settings.linkedin_url || ''} onChange={(e) => setSettings(s => ({...s, linkedin_url: e.target.value}))} placeholder="https://linkedin.com/company/bison-food-solutions" data-testid="linkedin-url" />
                </div>
                <div>
                  <Label>Latest post URLs (one per line, up to 3)</Label>
                  <Textarea rows={3} value={(settings.linkedin_posts || []).join('\n')} onChange={(e) => setSettings(s => ({...s, linkedin_posts: e.target.value.split('\n').map(x => x.trim()).filter(Boolean).slice(0, 3)}))} data-testid="linkedin-posts" />
                </div>
              </div>
              <div className="p-4 rounded-lg border border-border space-y-3">
                <div className="flex items-center gap-2 font-medium"><Instagram className="w-4 h-4 text-pink-600" />Instagram</div>
                <div>
                  <Label>Profile URL</Label>
                  <Input value={settings.instagram_url || ''} onChange={(e) => setSettings(s => ({...s, instagram_url: e.target.value}))} placeholder="https://instagram.com/bison_food" data-testid="instagram-url" />
                </div>
                <div>
                  <Label>Latest post URLs (one per line, up to 3)</Label>
                  <Textarea rows={3} value={(settings.instagram_posts || []).join('\n')} onChange={(e) => setSettings(s => ({...s, instagram_posts: e.target.value.split('\n').map(x => x.trim()).filter(Boolean).slice(0, 3)}))} data-testid="instagram-posts" />
                </div>
              </div>
              <Button onClick={saveSettings} disabled={savingSettings} data-testid="social-save"><Save className="w-4 h-4 mr-1" />{savingSettings ? 'Saving…' : 'Save'}</Button>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
